import React, {useEffect, useState} from "react";
import {Button, Descriptions, Modal, Spin, Tag} from "antd";
import {message} from "../../utils/antdFeedback";
import {ArrowLeft, ReceiptText} from "lucide-react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {AppDispatch, RootState} from "../../redux/store";
import {getBillPaymentById, refundFailedBillPayment, retryFailedBillPayment} from "../../redux/slice/billPaymentSlice";
import {HeroHeader, Card, formatNaira, COLORS} from "../../designSystem";

const statusLabel = (value: any) => {
  const status = Number(value);
  if (status === 2) return "Successful";
  if (status === 3) return "Failed";
  return "Pending";
};

const statusColor = (value: any) => {
  const status = Number(value);
  if (status === 2) return "green";
  if (status === 3) return "red";
  return "gold";
};

const sourceLabel = (value: any) => Number(value) === 1 ? "Wallet" : "VFD transfer";
const dateText = (value: string) => value ? new Date(value).toLocaleString() : "-";
const text = (value: any) => value || "-";
const errorText = (error: any) =>
  error?.message || error?.Message || error?.error?.message || "Request failed. Please try again.";
const requestEndpoint = (payment: any) => {
  if (payment?.providerRequestEndpoint) return payment.providerRequestEndpoint;
  if (!payment?.providerRequestJson) return payment?.providerEndpoint;

  const endpoint = String(payment?.providerEndpoint || "");
  if (endpoint.includes("/vtech-bills/api/v2/billspaymentstore/")) {
    return `${endpoint.split("/vtech-bills/api/v2/billspaymentstore/")[0]}/vtech-bills/api/v2/billspaymentstore/pay`;
  }

  return payment?.providerEndpoint;
};
const responseEndpoint = (payment: any) => payment?.providerResponseEndpoint || payment?.providerEndpoint;

const JsonBlock = ({value}: {value?: string}) => {
  if (!value) return <span>-</span>;

  let formatted = value;
  try {
    formatted = JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    formatted = value;
  }

  return (
    <div>
      <div className="mb-2 flex justify-end">
        <Button size="small" onClick={() => {
          navigator.clipboard.writeText(formatted);
          message.success("Copied");
        }}>
          Copy
        </Button>
      </div>
      <pre className="max-h-96 overflow-auto rounded-lg bg-slate-950 p-4 text-xs text-white">
        {formatted}
      </pre>
    </div>
  );
};

function BillPaymentDetails() {
  const {id} = useParams();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const {token} = useSelector((state: RootState) => state.account);
  const {selectedPayment, loading} = useSelector((state: RootState) => state.billPayments);
  const [refunding, setRefunding] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const isCurrentPayment = selectedPayment && String(selectedPayment.id) === String(id);

  useEffect(() => {
    if (token && id) {
      dispatch(getBillPaymentById({token, transactionId: id}));
    }
  }, [token, id, dispatch]);

  if (loading || !isCurrentPayment) {
    return <AdminLayout><div className="m-10"><Spin tip="Loading bill payment..."/></div></AdminLayout>;
  }

  const canRefund = Number(selectedPayment?.status) !== 2 &&
    Number(selectedPayment?.source) === 1 &&
    selectedPayment?.walletTransactionId &&
    !selectedPayment?.adminRefunded;
  const canRetry = Number(selectedPayment?.status) === 3 && !selectedPayment?.adminRefunded;

  const refundPayment = () => {
    if (!token || !id || !canRefund) return;

    Modal.confirm({
      title: "Refund failed bill payment",
      content: "This will credit the unsuccessful bill amount back to the user's wallet. Continue?",
      okText: "Refund wallet",
      onOk: async () => {
        setRefunding(true);
        try {
          await dispatch(refundFailedBillPayment({
            token,
            transactionId: id,
            reason: "Admin refund for failed bill payment",
          })).unwrap();
          message.success("Bill payment refunded to wallet");
        } catch (error: any) {
          message.error(errorText(error));
        } finally {
          setRefunding(false);
        }
      },
    });
  };

  const retryPayment = () => {
    if (!token || !id || !canRetry) return;

    Modal.confirm({
      title: "Retry failed bill payment",
      content: "This will submit the bill payment to VFD again using the same PayHive reference. Continue?",
      okText: "Retry payment",
      onOk: async () => {
        setRetrying(true);
        try {
          await dispatch(retryFailedBillPayment({
            token,
            transactionId: id,
          })).unwrap();
          message.success("Bill payment retry submitted");
        } catch (error: any) {
          message.error(errorText(error));
        } finally {
          setRetrying(false);
        }
      },
    });
  };

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        <HeroHeader
          icon={ReceiptText}
          title="Bill Payment Details"
          subtitle={selectedPayment?.reference || "Transaction details"}
          actions={
            <div className="flex flex-wrap gap-2">
              {canRefund ? (
                <Button type="primary" loading={refunding} disabled={refunding} onClick={refundPayment}>
                  Refund wallet
                </Button>
              ) : null}
              {canRetry ? (
                <Button loading={retrying} disabled={retrying} onClick={retryPayment}>
                  Retry payment
                </Button>
              ) : null}
              <Button icon={<ArrowLeft size={16}/>} onClick={() => navigate(-1)}>Back</Button>
            </div>
          }
        />

        <Card className="mt-8 p-5">
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Status">
              <Tag color={statusColor(selectedPayment?.status)}>{statusLabel(selectedPayment?.status)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Amount">{formatNaira(selectedPayment?.amount || 0)}</Descriptions.Item>
            <Descriptions.Item label="Source">{sourceLabel(selectedPayment?.source)}</Descriptions.Item>
            <Descriptions.Item label="Reference">{text(selectedPayment?.reference)}</Descriptions.Item>
            <Descriptions.Item label="Provider Transaction Id">{text(selectedPayment?.providerTransactionId)}</Descriptions.Item>
            <Descriptions.Item label="Latest VFD Endpoint">{text(selectedPayment?.providerEndpoint)}</Descriptions.Item>
            <Descriptions.Item label="Provider Status">{text(selectedPayment?.providerStatus)}</Descriptions.Item>
            <Descriptions.Item label="Provider Message">{text(selectedPayment?.providerMessage)}</Descriptions.Item>
            <Descriptions.Item label="Failure Reason">{text(selectedPayment?.failureReason)}</Descriptions.Item>
            <Descriptions.Item label="Token">{text(selectedPayment?.token)}</Descriptions.Item>
            <Descriptions.Item label="Created At">{dateText(selectedPayment?.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="Completed At">{dateText(selectedPayment?.completedAt)}</Descriptions.Item>
            <Descriptions.Item label="Last Checked At">{dateText(selectedPayment?.lastCheckedAt)}</Descriptions.Item>
          </Descriptions>
        </Card>

        <Card className="mt-6 p-5">
          <Descriptions title="User and Bill" bordered column={1}>
            <Descriptions.Item label="User Email">{text(selectedPayment?.userEmail)}</Descriptions.Item>
            <Descriptions.Item label="User Name">{text(selectedPayment?.userName)}</Descriptions.Item>
            <Descriptions.Item label="Category">{text(selectedPayment?.categoryName)}</Descriptions.Item>
            <Descriptions.Item label="Biller">{text(selectedPayment?.billerName || selectedPayment?.billerId)}</Descriptions.Item>
            <Descriptions.Item label="Product Id">{text(selectedPayment?.productId)}</Descriptions.Item>
            <Descriptions.Item label="Payment Item">{text(selectedPayment?.paymentItemName || selectedPayment?.paymentItem)}</Descriptions.Item>
            <Descriptions.Item label="Customer Id">{text(selectedPayment?.customerId)}</Descriptions.Item>
            <Descriptions.Item label="Customer Email">{text(selectedPayment?.customerEmail)}</Descriptions.Item>
            <Descriptions.Item label="Customer Phone">{text(selectedPayment?.customerPhone)}</Descriptions.Item>
          </Descriptions>
        </Card>

        <Card className="mt-6 p-5">
          <Descriptions title="Settlement" bordered column={1}>
            <Descriptions.Item label="Wallet Transaction Id">{text(selectedPayment?.walletTransactionId)}</Descriptions.Item>
            <Descriptions.Item label="Wallet Reference">{text(selectedPayment?.walletTransactionReference)}</Descriptions.Item>
            <Descriptions.Item label="Admin Refunded">{selectedPayment?.adminRefunded ? "Yes" : "No"}</Descriptions.Item>
            <Descriptions.Item label="Admin Refund Amount">{formatNaira(selectedPayment?.adminRefundAmount || 0)}</Descriptions.Item>
            <Descriptions.Item label="Admin Refund At">{dateText(selectedPayment?.adminRefundedAt)}</Descriptions.Item>
            <Descriptions.Item label="Admin Refund Reason">{text(selectedPayment?.adminRefundReason)}</Descriptions.Item>
            <Descriptions.Item label="VFD Transaction Id">{text(selectedPayment?.vfdTransactionId)}</Descriptions.Item>
            <Descriptions.Item label="VFD Account Name">{text(selectedPayment?.vfdAccountName)}</Descriptions.Item>
            <Descriptions.Item label="VFD Account Number">{text(selectedPayment?.vfdAccountNumber)}</Descriptions.Item>
            <Descriptions.Item label="VFD Bank">{text(selectedPayment?.vfdBankName || selectedPayment?.vfdBankCode)}</Descriptions.Item>
            <Descriptions.Item label="VFD Account Reference">{text(selectedPayment?.vfdAccountReference)}</Descriptions.Item>
            <Descriptions.Item label="VFD Account Created">{dateText(selectedPayment?.vfdAccountCreatedAt)}</Descriptions.Item>
            <Descriptions.Item label="VFD Account Expires">{dateText(selectedPayment?.vfdAccountExpiresAt)}</Descriptions.Item>
          </Descriptions>
        </Card>

        <Card className="mt-6 p-5">
          <Descriptions title="VFD Session" bordered column={1}>
            <Descriptions.Item label="Request Method">POST</Descriptions.Item>
            <Descriptions.Item label="Request Endpoint">{text(requestEndpoint(selectedPayment))}</Descriptions.Item>
            <Descriptions.Item label="Response Endpoint">{text(responseEndpoint(selectedPayment))}</Descriptions.Item>
            <Descriptions.Item label="Status">{text(selectedPayment?.providerStatus)}</Descriptions.Item>
            <Descriptions.Item label="Message">{text(selectedPayment?.providerMessage)}</Descriptions.Item>
          </Descriptions>
        </Card>

        <Card className="mt-6 p-5">
          <div className="mb-3 text-lg font-semibold">Transfer Verification</div>
          <Descriptions bordered column={1} className="mb-4">
            <Descriptions.Item label="Endpoint">{text(selectedPayment?.transferVerificationEndpoint)}</Descriptions.Item>
            <Descriptions.Item label="Transfer Status">{text(selectedPayment?.transferVerificationStatus)}</Descriptions.Item>
            <Descriptions.Item label="Transfer Message">{text(selectedPayment?.transferVerificationMessage)}</Descriptions.Item>
            <Descriptions.Item label="Verified At">{dateText(selectedPayment?.transferVerifiedAt)}</Descriptions.Item>
          </Descriptions>
          <JsonBlock value={selectedPayment?.transferVerificationResponseJson}/>
        </Card>

        <Card className="mt-6 p-5">
          <div className="mb-3 text-lg font-semibold">VFD Bill Pay Request</div>
          <Descriptions bordered column={1} className="mb-4">
            <Descriptions.Item label="Method">POST</Descriptions.Item>
            <Descriptions.Item label="Endpoint">{text(selectedPayment?.providerPayEndpoint || requestEndpoint(selectedPayment))}</Descriptions.Item>
          </Descriptions>
          <JsonBlock value={selectedPayment?.providerPayRequestJson || selectedPayment?.providerRequestJson}/>
        </Card>

        <Card className="mt-6 p-5">
          <div className="mb-3 text-lg font-semibold">VFD Bill Pay Response</div>
          <Descriptions bordered column={1} className="mb-4">
            <Descriptions.Item label="Endpoint">{text(selectedPayment?.providerPayEndpoint || responseEndpoint(selectedPayment))}</Descriptions.Item>
          </Descriptions>
          <JsonBlock value={selectedPayment?.providerPayResponseJson || selectedPayment?.providerResponseJson}/>
        </Card>

        <Card className="mt-6 p-5">
          <div className="mb-3 text-lg font-semibold">Latest Provider Response</div>
          <Descriptions bordered column={1} className="mb-4">
            <Descriptions.Item label="Endpoint">{text(responseEndpoint(selectedPayment))}</Descriptions.Item>
          </Descriptions>
          <JsonBlock value={selectedPayment?.providerResponseJson}/>
        </Card>
      </main>
    </AdminLayout>
  );
}

export default BillPaymentDetails;
